import { CampaignStatus, TaskStatus } from "./campaign-api";
import type { Session } from "./Store";

export const base = '/' //location.hostname.match(/localhost|172\.16|app|44./) ? '/' : '/wa/';

export const BotStatus = {
    idle : "Parado",
    initializing: "Conectando",
    qrcode: "Aguardando Ler QR-Code",
    online: "Online",
    offline: "Desconectado"
}

export interface Bot{
    id: number
    name: string
    picture?: string
    phone?: string
    qrcode?: number
    loadedProgress?: number
    token: string
    status: "idle" | "initializing" | "qrcode" | "online" | "offline" 
}

export async function load(url: string, post:any = null, token:string = null, doCache = false){
    var options:RequestInit = doCache ? {} : {cache:"no-cache"}
    var headers:any = doCache ? {} : {cache:"no-cache"};

    if(post){
        options.method = "post";
        if(post instanceof FormData) {
            options.body = post;
        } else {
            headers = {...headers, "content-type": "application/json"}
            options.body = JSON.stringify(post);
        }
    }

    if(token){
        headers = {...headers, "authorization": "bearer " + token}
    }

    options.headers = headers;

    const res = await fetch(base + url, options)

    if(res.status >= 400){
        let error = await res.json();
        throw error.error || error
    }

    return await res.json()
}

export async function listBots():Promise<Bot[]>{
    var result = await load("bots");
    if(result.error)
        throw result.error
    return result.bots;
}

export async function userInfo():Promise<Session>{
    var result = await load("user-info");
    if(result.error)
        throw result.error
    return result;
}

export async function login(email: string, password: string):Promise<Session>{
    var result = await load("login", {email, password});
    if(result.error)
        throw result.error
    return result;
}

export async function loadStatus(bot: Bot):Promise<Bot> {
    var result = await load(`bots/${bot.id}/qrcode`, null, bot.token);
    if(result.error)
        throw result.error
    return result;
}

export async function logoutBot(bot: Bot):Promise<Bot> {
    var result = await load(`bots/${bot.id}/logout`, null, bot.token);
    if(result.error)
        throw result.error
    return result;
}

export async function resetBot(bot: Bot):Promise<Bot> {
    var result = await load(`bots/${bot.id}/reset`, null, bot.token);
    if(result.error)
        throw result.error
    return result;
}

export async function stopBot(bot: Bot):Promise<Bot> {
    var result = await load(`bots/${bot.id}/stop`, null, bot.token);
    if(result.error)
        throw result.error
    return result;
}

export async function startBot(bot: Bot){
    var result = await load(`bots/${bot.id}/start`, null, bot.token);
    if(result.error)
        throw result.error
}

export async function pairingCode(bot: Bot, phone: string){
    var result = await load(`bots/${bot.id}/pairing-code`, {phone}, bot.token);
    if(result.error)
        throw result.error
    if(!result.pairingCode)
        throw "Erro ao requisitar código";
    return result.pairingCode as string;
}

export async function sendMessage(bot: Bot, message:{to:string, message:string, doc?:any}){
    var result = await load(`bot/${bot.id}/send-message`, message, bot.token);
    if(result.error)
        throw result.error
}

export interface TContact {
    id: string
    name: string
    time: Date | string
}

export interface LastMessage {
    fromMe: boolean
    body: string
    type: string
    timestamp: number
}

export async function getTodayReport(bot: Bot){
    const response = await load(`bots/${bot.id}/today-interaction-count`, null, bot.token)
    const result = response.result as TContact[];
    return result.map( a => ({...a, time: new Date(a.time)})) 
}

export async function getDailyReport(bot: Bot):Promise<[string, string, number][]>{
    const response = await load(`bots/${bot.id}/daily-report`, null, bot.token, true)
    return response.report
}

export async function termToContact(bot: Bot, term: string){
    const response = await load(`bots/${bot.id}/term-to-contact`, {search: term}, bot.token)
    const result = response.result as TContact[];
    return result as TContact[]
}


export async function getLastMessage(bot: Bot, to:string){
    to = to.replace(/[^\d]/g, '');
    const response = await load(`bots/${bot.id}/last-message/${to}`, null, bot.token)
    return response.result as LastMessage
}

export async function downloadContactsCSV(bot: Bot){
    const response = await load(`bots/${bot.id}/contacts-csv`, null, bot.token)
    return response.result as string
}

export async function downloadGroupsCSV(bot: Bot){
    const response = await load(`bots/${bot.id}/groups-csv`, null, bot.token)
    return response.result as string
}

export async function exportContacts(bot: Bot, request:any):Promise<{eventsToken:string}>{
    const response = await load(`bots/${bot.id}/export-contacts`, request, bot.token)
    return response;
}

export type Tag = {
    value: string
    label: string
    color: string
}

export async function getExportInfo(bot: Bot):Promise<{token?:string, tags?: Tag[]}>{
    const response = await load(`bots/${bot.id}/export-contacts-info`, null, bot.token)
    return response;
}

export type ExportEvent = {
    total?: number,
    current?: number,
    error?: number
    phase: "reading" | "saving" | "starting",
    done?: boolean
    errorMessage?: string
    file?: string
}

type BotEvents = {
   'export-contacts': ExportEvent
   'campaign-status': {taskStatus: TaskStatus, status: CampaignStatus}
}

type PropType<T, Prop extends keyof T> = T[Prop];

export function useBotEvents<K extends keyof BotEvents>(token: string, event:K, listener: (event: PropType<BotEvents, K>) => void, errorHandler: () => void) {
    const url = `${base}events/${event}?token=${token}`;
    console.log('url', );
    const eventSource = new EventSource(url);

    eventSource.addEventListener('botEvent', (event: MessageEvent) => {
    const eventData = JSON.parse(event.data) as PropType<BotEvents, K>;
    listener(eventData);
    });

    eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        eventSource.close();
        errorHandler();
    };

    return () => {
        console.log('event-source-closed')
        eventSource.close();
    };
}